import {useContext, useRef, useState} from "react";
import {Column, TID} from "./Table";
import {useSnackbar} from "../Snackbar";
import {EditContext} from "./EditProvider";
import {EventEmitter} from "nate-react-api-helpers";

const lighthouseEmitter = new EventEmitter<boolean>();
let lighthouseCounter = 0;

document.body.addEventListener("dragenter", (e) => {
    if(lighthouseCounter === 0) {
        if(lighthouseEmitter.lastValue === true) return;
        lighthouseEmitter.emit(true);
    }

    lighthouseCounter++;
})

document.body.addEventListener("dragleave", (e) => {
    if(lighthouseCounter > 0) lighthouseCounter--

    if(lighthouseCounter === 0) {
        if(lighthouseEmitter.lastValue === false) return;
        lighthouseEmitter.emit(false);
    }
})

document.body.addEventListener("drop", (e) => {
    lighthouseCounter = 0;
    lighthouseEmitter.emit(false);
});

export function useDragDrop<T extends TID>(props: {column: Column<T>, value: T}) {
    const column = props.column;
    // resolve the per-row editable so columns using editableFunc keep file-drop upload.
    const editable = column.editable ?? column.editableFunc?.(props.value)?.editable ?? undefined;
    const hasDragOver = !!editable?.fileDropAction;
    const [dragOver, setDragOver] = useState(false);
    const snack = useSnackbar()
    const {update} = useContext(EditContext);
    const enterCountRef = useRef(0);
    const lighthouse = EventEmitter.reactValue(lighthouseEmitter);

    return {
        isDraggingOver: dragOver,
        isLighthouse: hasDragOver && lighthouse,
        hasDragOver: hasDragOver,
        onDragOver: orBlank(hasDragOver, d => {
            d.preventDefault();
        }),
        onDragEnter: orBlank(hasDragOver, d => {
            d.preventDefault();
            if(enterCountRef.current === 0) setDragOver(true);
            enterCountRef.current++
        }),
        onDragLeave: orBlank(hasDragOver, (d) => {
            d.preventDefault()
            if(enterCountRef.current > 0) enterCountRef.current--
            if(enterCountRef.current === 0) setDragOver(false);
        }),
        onDrop: orBlank(hasDragOver, async (d) => {
            if(!editable?.fileDropAction) return;
            const uploadAction = editable.fileDropAction;

            d.preventDefault();

            enterCountRef.current = 0;
            setDragOver(false);

            const files = d.dataTransfer.files;
            const accept = editable?.fileDropAccept || [];

            let list = Array.from(files) as File[];
            if(accept.length > 0) {
                list = list.filter(f => {
                    return !!accept.find(a => mimeMatches(a, f.type));
                });
            }

            if(list.length === 0) return;

            snack.loading("Uploading...");

            try {
                await uploadAction(list, {
                    row: props.value,
                    update: update,
                });

                snack.hide()
            } catch (e: any) {
                snack.error(e.toString())
            }
        })
    }
}

function orBlank<T>(tf: boolean, callback: T): T | undefined {
    if(tf) return callback;
    return undefined;
}

function mimeMatches(pattern: string, value: string) {
    if(pattern === "*/*") {
        return true;
    }

    if(pattern.endsWith("/*")) {
        return value.startsWith(pattern.replace(/\/\*$/, ""))
    }

    return pattern === value;
}